import { PlusOutlined, RightOutlined } from "@ant-design/icons"
import { Button, Popconfirm, Spin, Tooltip, message } from "antd"
import moment from "moment"
import React, { FC, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import { naturalLanguageAge } from "../../helpers/natualLanguageAge"
import { getStoredBookerId } from "../../helpers/getStoredBookerId"
import {
    Booker,
    BookerAttendeeInput,
    GetUpdatedBookerDocument,
    GetUpdatedBookerQuery,
    useDeleteAttendeeMutation,
    useGetUpdatedBookerQuery,
} from "../../generated/graphql"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import { URL_ACCOUNT_ATTENDEES, URL_ACCOUNT_PROFILE } from "../../models/url"
import CreateEditAttendee from "../../views/CreateEditAttendee/CreateEditAttendee"
import { collectCourseLessonIds, useAttendeeEditLock } from "../../views/SelectAttendeeTable/useAttendeeEditLock"
import { SubHeader } from "./AccountDashboard"
import Tarif595AttendeeData from "./Tarif595AttendeeData"
import css from "./AccountAttendees.module.css"

type Attendee = NonNullable<NonNullable<GetUpdatedBookerQuery["booker"]>["attendees"]>[number]
type EditState = { mode: "create" } | { mode: "edit"; attendee: Attendee } | null

const initial = (firstname?: string | null, lastname?: string | null): string => (firstname || lastname || "?").charAt(0)

// Modul-Level-Komponente (nicht inline im Render), damit der Hook pro Zeile
// laufen kann und die Zeilen nicht bei jedem Render remounten.
const AttendeeRow: FC<{
    attendee: Attendee
    bookerCustomerId?: string | null
    deleting: boolean
    onEdit: () => void
    onDelete: () => void
    /** Kurslektions-IDs ALLER Zeilen — gleiche Query-Variablen je Zeile → Apollo dedupliziert auf einen Request. */
    allCourseLessonIds?: string[]
}> = ({ attendee, bookerCustomerId, deleting, onEdit, onDelete, allCourseLessonIds }) => {
    const { t } = useTranslation()
    // Gleiche Regeln wie im Buchungs-Wizard (ActionButtons): Bearbeiten gesperrt,
    // sobald eine gebuchte Kurslektion bereits begonnen hat — und der Bucher
    // selbst wird über sein Profil bearbeitet, nicht als Teilnehmer.
    const { blocked, editTooltip } = useAttendeeEditLock(attendee, allCourseLessonIds)
    const attendeeIsBooker = !!bookerCustomerId && attendee.customer.id === bookerCustomerId
    const editDisabledText = attendeeIsBooker
        ? t("account.attendees.bookerNotEditable", "Der Bucher kann hier nicht bearbeitet werden. Gehe dazu in dein Profil.")
        : editTooltip

    const name = [attendee.firstname, attendee.lastname].filter(Boolean).join(" ")
    const activeBookings = (attendee.bookings ?? []).filter((b) => !b?.canceled).length
    const age = attendee.birthday ? naturalLanguageAge(moment().diff(attendee.birthday, "months")) : undefined
    const bookingsLabel =
        activeBookings === 1
            ? t("account.attendees.bookingsCountOne", { count: activeBookings, defaultValue: "{{count}} Buchung" })
            : t("account.attendees.bookingsCountOther", { count: activeBookings, defaultValue: "{{count}} Buchungen" })

    return (
        <div className={css.row} data-testid='account-attendee-row'>
            <span className={css.avatar}>{initial(attendee.firstname, attendee.lastname)}</span>
            <span className={css.body}>
                <span className={css.name}>{name}</span>
                <span className={css.meta}>{[age, bookingsLabel].filter(Boolean).join(" · ")}</span>
            </span>
            <span className={css.actions}>
                <Tooltip title={editDisabledText} placement='top'>
                    <Button size='small' type='link' onClick={onEdit} disabled={blocked || attendeeIsBooker} data-testid='account-attendee-edit'>
                        {t("account.attendees.edit", "Bearbeiten")}
                    </Button>
                </Tooltip>
                {attendee.deletable && (
                    <Popconfirm
                        title={t("account.attendees.deleteConfirm", "Diesen Teilnehmer wirklich löschen?")}
                        okText={t("account.attendees.delete", "Löschen")}
                        cancelText={t("account.attendees.cancel", "Abbrechen")}
                        okButtonProps={{ danger: true, loading: deleting }}
                        onConfirm={onDelete}
                    >
                        <Button size='small' type='link' danger data-testid='account-attendee-delete'>
                            {t("account.attendees.delete", "Löschen")}
                        </Button>
                    </Popconfirm>
                )}
            </span>
        </div>
    )
}

const AccountAttendees: FC = () => {
    const { t } = useTranslation()
    const [editState, setEditState] = useState<EditState>(null)
    const tarif595Enabled = useContext(SettingsContext).tarif595Enabled === true
    const bookerId = getStoredBookerId()

    const { data, loading, error, refetch } = useGetUpdatedBookerQuery({
        skip: !bookerId,
        variables: { id: bookerId ?? "" },
        fetchPolicy: "cache-and-network",
    })
    const [deleteAttendee, { loading: deleting }] = useDeleteAttendeeMutation({
        refetchQueries: [{ query: GetUpdatedBookerDocument, variables: { id: bookerId ?? "" } }],
    })

    const booker = data?.booker
    const title = t("account.attendees.title", "Teilnehmer")

    if (loading && !booker) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <div className={css.loading} data-testid='account-attendees-loading'>
                    <Spin spinning />
                </div>
            </div>
        )
    }

    if (error && !booker) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <QueryErrorAlert testId='account-attendees-error' message={t("account.loadError", "Dein Benutzerkonto konnte nicht geladen werden.")} onRetry={() => refetch()} />
            </div>
        )
    }

    if (!booker) return null

    if (editState) {
        return (
            <div className={css.page} data-testid='account-attendees-form'>
                <SubHeader
                    title={editState.mode === "edit" ? t("account.attendees.editTitle", "Teilnehmer bearbeiten") : t("account.attendees.createTitle", "Teilnehmer anlegen")}
                    onBack={() => setEditState(null)}
                    backLabel={t("account.backTo", { target: t("account.attendees.title", "Teilnehmer"), defaultValue: "Zurück zu {{target}}" })}
                />
                <CreateEditAttendee
                    booker={booker as unknown as Booker}
                    editMode={editState.mode === "edit"}
                    attendeeToEdit={editState.mode === "edit" ? (editState.attendee as unknown as BookerAttendeeInput) : undefined}
                    onOk={() => {
                        setEditState(null)
                        refetch()
                    }}
                    onCancel={() => setEditState(null)}
                />
            </div>
        )
    }

    const attendees = booker.attendees ?? []
    // Einmal pro Render berechnen, nicht pro Zeile im map
    const allCourseLessonIds = collectCourseLessonIds(attendees)

    const handleDelete = async (attendeeId: string) => {
        try {
            await deleteAttendee({ variables: { id: attendeeId } })
            message.success(t("account.attendees.deleteSuccess", "Teilnehmer wurde gelöscht."))
        } catch (err) {
            const raw = getGraphQLErrorMessage(err) || ""
            message.error(raw.includes("attendee has bookings") ? t("account.attendees.deleteHasBookings", "Teilnehmer mit Buchungen kann nicht gelöscht werden.") : t("account.attendees.deleteError", "Der Teilnehmer konnte nicht gelöscht werden."))
        }
    }

    const bookerName = [booker.firstname, booker.lastname].filter(Boolean).join(" ") || t("account.attendees.accountHolder", "Kontoinhaber")

    return (
        <div className={css.page} data-testid='account-attendees'>
            <SubHeader title={title} />
            <div className={css.list}>
                {attendees.map((attendee) => (
                    <AttendeeRow
                        key={attendee.id}
                        attendee={attendee}
                        bookerCustomerId={booker.customerId}
                        deleting={deleting}
                        onEdit={() => setEditState({ mode: "edit", attendee })}
                        onDelete={() => handleDelete(attendee.id)}
                        allCourseLessonIds={allCourseLessonIds}
                    />
                ))}

                <Link
                    // fromUrl: Profil-Zurück-Pfeil führt zur Teilnehmerliste zurück statt zum Dashboard
                    to={{ pathname: URL_ACCOUNT_PROFILE, state: { fromUrl: { pathname: URL_ACCOUNT_ATTENDEES } } }}
                    className={css.selfLink}
                    data-testid='account-attendee-self'
                >
                    <div className={css.row}>
                        <span className={css.avatar}>{initial(booker.firstname, booker.lastname)}</span>
                        <span className={css.body}>
                            <span className={css.name}>{bookerName}</span>
                            <span className={css.meta}>{t("account.attendees.yourself", "Du selbst")}</span>
                        </span>
                        <RightOutlined />
                    </div>
                </Link>
            </div>

            <Button type='dashed' block icon={<PlusOutlined />} onClick={() => setEditState({ mode: "create" })} data-testid='account-attendee-add'>
                {t("account.attendees.addAttendee", "Teilnehmer hinzufügen")}
            </Button>

            {/* key: Nach Anlegen oder Löschen neu laden */}
            {tarif595Enabled && <Tarif595AttendeeData key={attendees.length} />}
        </div>
    )
}

export default AccountAttendees
